import isNil from 'lodash/isNil';

export const getPrice = (value) => {
  const options = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  if (value) {
    return `$${value.toLocaleString('en', options)}`;
  }
  return '$0.00';
};

export const keysToLowerCase = (obj) => Object.fromEntries(
  Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value]),
);

export const emptyObject = Object.freeze({});
export const returnEmptyObject = () => emptyObject;

export const emptyArray = Object.freeze([]);

/**
 * Checks if a value is null, undefined, an empty string or array
 *
 * @param {null | undefined | string | Array<any>} value
 * @returns {boolean}
 */
export const isEmpty = (value) => (isNil(value) || value === '' || value.length === 0);

/**
 * Recursively removes all properties in objects and arrays that begin with an underscore.
 *
 * @param {Object|Array} data - The input data which can be an object or an array.
 * @returns {Object|Array} - The cleaned data with all properties starting with an underscore removed.
 * @example
 * // Example usage:
 * // create additional form states not meant for db persistence
 * const buildForm = {
 *   _private: 'should be removed',
 *   public: 'should stay',
 *   nested: {
 *     _privateNested: 'should be removed',
 *     publicNested: 'should stay',
 *     array: [
 *       { _privateArray: 'should be removed', publicArray: 'should stay' },
 *       'string',
 *       123,
 *     ],
 *   },
 * };
 * // don't send additional form states to the db
 * const formatData = stripPrivates(buildForm);
 * console.log(cleanedData);
 * // Output:
 * // {
 * //   public: 'should stay',
 * //   nested: {
 * //     publicNested: 'should stay',
 * //     array: [
 * //       { publicArray: 'should stay' },
 * //       'string',
 * //       123,
 * //     ],
 * //   },
 * // }
*/
export function stripPrivates(data) {
  if (Array.isArray(data)) {
    return data.map(stripPrivates);
  } if (data !== null && typeof data === 'object') {
    return Object.keys(data).reduce((acc, key) => {
      if (!key.startsWith('_')) {
        acc[key] = stripPrivates(data[key]);
      }
      return acc;
    }, {});
  }
  return data;
}

/**
 * Checks if a string contains digits only.
 *
 * @param {string} str
 * @returns {boolean}
 */
export const hasDigitsOnly = (str) => /^\d+$/.test(str ?? '');

/**
 * Checks if object's fields has any value
 * @param {object} obj
 * @returns {boolean}
 */
export const anyFieldHasValue = (obj) => {
  if (obj != null && !Array.isArray(obj) && typeof obj === 'object') {
    return Object.values(obj).findIndex((x) => (typeof x === 'string' ? !!x : x != null)) > -1;
  }

  return false;
};